import { useEffect, useState } from 'react';
import { IconMaterial } from '../shared/iconMaterial/IconMaterial';
import { FullNameCeil } from '../table-user/FullNameCeil';
import Breadcrumbs from '../breadcrumbs/Breadcrumbs';
import { useNavigate } from 'react-router';
import { useDashboard } from '../../context/DashboardProvider';
import { GB, RU, KZ } from 'country-flag-icons/react/3x2'
import { useSessionEnd } from '../../context/AuthProvider';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import Badge from '../shared/ui/Badge';
import Button from '../shared/ui/Button/baseBtn';

export type TCrumb = {
    name: string;
    path?: string
}
type THeader = {
    title: string,
    crumbs?: TCrumb[],
    status?: any,
    create?: string;
}

const Header = ({ title, crumbs, create, status }: THeader) => {

    const { t } = useTranslation()
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showLanguageSubmenu, setShowLanguageSubmenu] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("English");
    const { profile, changeLanguage } = useDashboard();
    const sessionEnd = useSessionEnd();

    const languages = [
        { code: 'en', name: 'English', flag: <GB /> },
        { code: 'ru', name: 'Russian', flag: <RU /> },
        { code: 'kk', name: 'Kazakh', flag: <KZ /> }
    ];

    const handleLanguageSelect = (language: string, code: string) => {
        changeLanguage(code)
        localStorage.setItem('language', code);
        setSelectedLanguage(language);
        setShowLanguageSubmenu(false);
    };

    const getSelectedLanguage = (language?: string) => {
        if (language === "ru") {
            return 'Russian';
        }
        else if (language === "kk") {
            return 'Kazakh';
        }
        return 'English';
    }

    useEffect(() => {
        if (i18n.isInitialized) {
            const storedLang = localStorage.getItem('language');
            if (storedLang) {
                i18n.changeLanguage(storedLang);
                setSelectedLanguage(getSelectedLanguage(storedLang));
            } else if (profile?.language) {
                const code = profile.language;
                i18n.changeLanguage(code);
                localStorage.setItem('language', code);
                setSelectedLanguage(getSelectedLanguage(code));
            }
        }
        else {
            setSelectedLanguage(getSelectedLanguage(profile?.language))
        }
    }, [profile?.language, i18n.isInitialized]);


    const handleSignOut = () => {
        sessionEnd()
        navigate('/')
    }

    return (
        <div className="bg-gray-50">
            <header className="bg-white py-2 px-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <h1 className="text-lg font-medium leading-[28px] not-italic">
                            {title} {status && <Badge status={status} />}
                        </h1>
                        {
                            create && profile?.clinic_role == 'Super admin' &&
                            <Button
                                onClick={() => navigate(create)}
                                className="cursor-pointer"
                                style={{ borderRadius: "100%", padding: "8px" }}
                            >
                                <IconMaterial
                                    filled
                                    icon='add'
                                    size={20}
                                    iconColor="var(--tokens-text-secondary-text)"
                                />
                            </Button>
                        }
                    </div>
                    <div className="relative">
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className={`flex p-1 rounded-lg text-primeblue ${isDropdownOpen && 'bg-background-light-gray'} hover:bg-background-light-gray transition-colors duration-200`}
                        >
                            <FullNameCeil className='bg-primary text-white' user={profile} />
                            <IconMaterial
                                filled
                                icon={isDropdownOpen ? 'expand_less' : 'expand_more'}
                                className="cursor-pointer mt-1"
                                size={20}
                                iconColor={isDropdownOpen ? '#305997' : "var(--tokens-text-secondary-text)"}
                            />
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute right-0 p-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                <div onClick={() => { navigate('/accountsettings/') }} className="p-2 mb-1 cursor-pointer rounded-lg group hover:bg-background-light-gray">
                                    <p className="text-sm font-medium text-gray-900 leading-[22px] group-hover:text-primeblue">{t('profile.account-settings')}</p>
                                    <p className="text-sm text-gray-500">{t('profile.manage-account')}</p>
                                </div>

                                <div className="relative">
                                    <button
                                        onClick={() => setShowLanguageSubmenu(!showLanguageSubmenu)}
                                        className="w-full p-2 mb-2 flex items-center justify-between transition-colors rounded-lg group hover:bg-background-light-gray"
                                    >
                                        <div className="flex flex-col items-start">
                                            <p className="text-sm font-medium text-gray-900 leading-[22px] group-hover:text-primeblue">
                                                {t('profile.change-language')}
                                            </p>
                                            <span className="text-sm text-gray-500">
                                                {selectedLanguage}
                                            </span>
                                        </div>
                                        <IconMaterial
                                            filled
                                            icon={showLanguageSubmenu ? 'expand_less' : 'expand_more'}
                                            className="cursor-pointer"
                                            size={20}
                                            iconColor={"var(--tokens-text-secondary-text)"}
                                        />
                                    </button>

                                    {showLanguageSubmenu && (
                                        <div className="w-full bg-white p-2 mt-[-8px]">
                                            <div className='flex flex-col border-l-[2px] w-full gap-[4px] border-primary-stroke pl-2'>
                                                {languages.map((language) => (
                                                    <button
                                                        key={language.code}
                                                        onClick={() => handleLanguageSelect(language.name, language.code)}
                                                        className={`w-full p-2 flex items-center justify-between transition-colors rounded-lg group
                                                            ${selectedLanguage === language.name ? 'bg-background-light-gray' : 'hover:bg-background-light-gray'}`}
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            <span className="w-5 inline-block">{language.flag}</span>
                                                            <span className={`text-primary-light font-medium font-montserrat text-sm leading-[22px] group-hover:text-primeblue
                                                                ${selectedLanguage === language.name && 'text-primeblue'}`}>
                                                                {language.name}
                                                            </span>
                                                        </div>
                                                        {selectedLanguage === language.name && (
                                                            <div className='rounded-full mr-1 bg-primary text-white flex items-center justifiy-center'>
                                                                <IconMaterial
                                                                    filled
                                                                    icon={'check'}
                                                                    className="cursor-pointer"
                                                                    size={16}
                                                                    iconColor={"var(--tokens-text-secondary-text)"}
                                                                />
                                                            </div>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="border-t border-background-light-gray pt-2">
                                    <button
                                        onClick={handleSignOut}
                                        className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-background-light-gray hover:text-primeblue transition-colors duration-150 group">
                                        <IconMaterial
                                            filled
                                            icon='logout'
                                            className="cursor-pointer text-primary-light group-hover:text-primeblue"
                                            size={20}
                                        />
                                        <span>{t('profile.logout')}</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {
                    crumbs &&
                    <div className=" border-gray-100">
                        <Breadcrumbs crumbs={crumbs} />
                    </div>
                }
            </header>


            {isDropdownOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsDropdownOpen(false)}
                />
            )}
        </div>
    );
};

export default Header;